import { Button } from '@seen/ui'
import { IconSparkles } from '@tabler/icons-react'
import type { JSX } from 'react'
import type { ValidatedToolSuggestionControls } from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'

/* The tool-level controls for a staged AI suggestion, above the form's fields.
 *
 * Undo all sits on the LEFT, beside the label, while Accept all and Reject all
 * are right-aligned. That is load-bearing rather than cosmetic: the row
 * collapses to Undo all alone the moment the last pending field is decided, and
 * with all three right-aligned that collapse dropped Undo all into the
 * footprint Accept all had occupied a frame earlier — so a double-press on
 * Accept all accepted and then silently undid. Anchoring Undo all to the left
 * means no control ever moves under the pointer. */
export function ValidatedToolSuggestionHeader({
  controls,
}: {
  controls: ValidatedToolSuggestionControls | undefined
}): JSX.Element | null {
  const { acceptAll, rejectAll, undoAll } = controls ?? {}
  if (!acceptAll && !rejectAll && !undoAll) {
    return null
  }
  return (
    <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
      <IconSparkles className="size-4 shrink-0" />
      <span className="font-medium">Suggested by Orbit AI</span>
      {undoAll && (
        <Button type="button" size="sm" variant="ghost" onClick={undoAll}>
          Undo all
        </Button>
      )}
      <div className="ml-auto flex items-center gap-2">
        {rejectAll && (
          <Button type="button" size="sm" variant="outline" onClick={rejectAll}>
            Reject all
          </Button>
        )}
        {acceptAll && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            color="blue"
            onClick={acceptAll}
          >
            Accept all
          </Button>
        )}
      </div>
    </div>
  )
}
